import type { WorkflowCompletion } from '@generated/graphql';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import { MULTI_PORTFOLIO_DEPOSIT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import { MultiDepositEft } from './eft/MultiDepositEft';
import { useMultiDepositEft } from './eft/useMultiDepositEft';
import { MultiDepositFormHeader } from './MultiDepositFormHeader';

export type MultiDepositFormProp = {
  options: any;
  onNext: () => void | Promise<void>;
  stepLoading?: boolean;
  workflowCompletion?: WorkflowCompletion;
  dummyDeposit?: any;
  setDummyDeposit?: any;
};

export const MultiDepositForm = ({
  options,
  onNext,
  workflowCompletion,
  dummyDeposit,
  setDummyDeposit,
}: MultiDepositFormProp) => {
  const { t } = useTranslation('workflowCompletions');
  const { userId: paramsUserId } = useParams();
  const { activeEntity } = useContext(UserContext);
  const userId = paramsUserId ?? activeEntity?.id;

  const [focused, setFocused] = useState<string[]>([]);

  const { applicableData: depositData, updateContextWithFields } =
    useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);

  useEffect(() => {
    if (depositData?.type || !options?.defaultTransferType) return;
    updateContextWithFields({ type: options.defaultTransferType });
  }, []);

  const isEft = depositData?.type === DEPOSIT_TRANSFER_TYPE.EFT;

  const eftForm = useMultiDepositEft({
    options,
    deposit: depositData,
    updateContextWithFields,
    userId,
  });

  const missingFields = isEft ? eftForm.missingFields : [];

  const focus = (field: string) => setFocused([...focused, field]);

  const showsFieldError = (field: string) =>
    focused.includes(field) && missingFields.includes(field);

  const submit = async () => {
    if (missingFields.length > 0) {
      setFocused(missingFields);
      return;
    }
    if (isEft && !eftForm.schedule.validTransferDelayCheck) {
      setFocused(['scheduledDate']);
      return;
    }
    await onNext();
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        dataTestId: MULTI_PORTFOLIO_DEPOSIT_TEST_ID.CONTINUE_BUTTON,
      }}
    >
      <MultiDepositFormHeader options={options} />
      {isEft && (
        <MultiDepositEft
          options={options}
          deposit={depositData}
          updateContextWithFields={updateContextWithFields}
          userId={userId}
          form={eftForm}
          focused={focused}
          focus={focus}
          showsFieldError={showsFieldError}
        />
      )}
    </WorkflowCompletionForm>
  );
};

export default MultiDepositForm;
